export const STATUS_VALUES = [
  'Draft',
  'RequestInitiated',
  'VerifyingWorker',
  'ReviewWorkerInfo',
  'WorkerInfoReviewed',
  'RequestAccepted',
  'Canceled',
  // VMS-only: pre-verification attempt was Discarded on the gw-sync side.
  // Distinct from Canceled (whole VMS request dead) — Rejected means the
  // pre-verification was thrown out but the underlying VMS row is still
  // actionable; the kebab re-exposes Start Pre-verification.
  'Rejected',
  'OnHold',
  // OB Portal onboarding status surfaced by the related-requests panel for Request-source rows
  // (the gw-sync Request family). Not a native client-request lifecycle status, but reused here so
  // it gets a label + badge like the rest.
  'InProgress',
] as const

export type ClientRequestStatus = (typeof STATUS_VALUES)[number]

export const REQUEST_TYPES = [
  'Onboarding',
  'General',
  'Extension',
  'Redeployment',
  'Termination',
  'Other',
] as const

export type ClientRequestType = (typeof REQUEST_TYPES)[number]

// API returns camelCase request type values — map them to the display ClientRequestType
export const API_REQUEST_TYPE_MAP: Record<string, ClientRequestType> = {
  onboarding: 'Onboarding',
  general: 'General',
  extension: 'Extension',
  redeployment: 'Redeployment',
  termination: 'Termination',
  other: 'Other',
}

export interface FacetValue {
  value: string
  count: number
}

export interface ClientRequestDetailFileDto {
  name: string
  fileStorageId: string
  extension: string
  size: string
}

export interface ClientRequestCommentDto {
  comment: string
  createdByUserName: string
  createdByUserEmail: string
  createdDateTime: string
}

export interface ClientRequestDetailDto {
  id: string
  requestType: string
  status: string
  requestDetails: string | null
  workerFullName: string | null
  clientOrganization: string | null
  assignedToUserEmail: string | null
  assignedToUserName: string | null
  fboAssignmentId: number | null
  primaryBillRateUnitCode: string | null
  workerOrganization: string | null
  lineOfBusiness: string | null
  workerProfileType: string | null
  startDate: string | null
  endDate: string | null
  billRate: number | null
  billCurrencyCode: string | null
  billRateTypeCode: string | null
  payRate: number | null
  payRateCurrencyCode: string | null
  payRateTypeCode: string | null
  terminationDetails: string | null
  jobTitle: string | null
  hiringManagerName: string | null
  hiringManagerEmail: string | null
  attachments: ClientRequestDetailFileDto[]
  requestedByName: string | null
  requestedByEmail: string | null
  comments: ClientRequestCommentDto[]
  actionedByUserName: string | null
  actionedByUserEmail: string | null
}

// Discriminates a row in the related-requests sidepanel between the legacy
// ClientRequest index, the ATS placement-request index, VMS requests (which
// share the gw-client-request-index with ClientRequest but get surfaced
// separately for their own detail card), and PreOnboardRequest rows mapped
// directly from AutomationShadowEntities — that entity has no search index, so
// the BE projects from the shadow snapshot and the panel falls through to the
// requestId-keyed lazy-fetch path (OnboardingDetailContent → OB Portal
// Request/{id}). Drives which detail component renders inside
// RelatedRequestSection.
export type RelatedRequestSource = "ClientRequest" | "AtsPlacementRequest" | "VmsRequest" | "PreOnboardRequest"

export interface ChangeRequestDto {
  // Discriminator — backend RelatedRequestDto.Source
  source?: RelatedRequestSource

  // Grid-level fields
  changeRequestId?: string
  status?: ClientRequestStatus
  workerName?: string
  clientOrg?: string
  workerOrg?: string
  lineOfBusiness?: string
  profileType?: string
  billRate?: number
  version?: number

  // New fields
  requestId?: string
  requestedByName?: string
  requestedByEmail?: string
  dateRequested?: string
  dateCompleted?: string

  // Grid column fields
  requestType?: ClientRequestType
  startDate?: string
  endDate?: string

  // ATS-only fields (populated when source === "AtsPlacementRequest")
  atsPlacementId?: number
  atsSource?: string
  jobTitle?: string
  preVerificationRequestId?: string
  atsStatus?: string
  workerEmail?: string

  // VmsRequest-only — null on other Source families. Powers the "View
  // original email" affordance on the related-requests panel for VMS rows.
  linkedEmailId?: string

  // AssignedTo + subtype fields (added per P2.9b)
  assignedToUserFullName?: string
  assignedToUserEmail?: string
  requestSubType?: string
}

// New search params type matching the real API contract (no $ prefix)
export interface ClientRequestSearchParams {
  search?: string
  filter?: string
  orderBy?: string
  top?: number
  skip?: number
  select?: string
  facets?: string[]
  count?: boolean
}

// API response type matching the OData envelope from the real endpoint
export interface ClientRequestSearchResponse {
  value: ClientRequestSearchResult[]
  '@odata.count': number
  '@search.facets'?: Record<string, FacetValue[]>
}

// Raw search result from the Azure Search index (PascalCase API fields mapped
// to camelCase). Canonical-only: the legacy `workerName` / `jobStartDate` /
// `jobEndDate` / `primaryBillRate` fields have been retired now that the
// ClientRequest reindex has been run across every environment and our writes
// populate the canonical fields. If a future migration needs the same
// dual-read pattern, layer the fallback at the result-mapper level rather
// than re-declaring deprecated raw fields here.
//
// Client carrier: as part of the gw-sync index alignment the flat
// `clientOrganization` string was retired from the index in favour of the
// structured `Client` complex type. The TS shape mirrors the index — the
// nested object is camelCased by the API serializer (`Client → client`,
// `Client.Name → client.name`). Read `client?.name` for the human-facing
// label; `client?.fboId` / `client?.obCode` are populated on VMS rows that
// went through the FBO client-match step, null on native ClientRequest rows
// that only carry a free-text name on the source doc.
export interface ClientRequestSearchResult {
  id: string | null
  requestId: string | null
  // Onboarding pre-verification linkage. For Onboarding ClientRequest rows this is
  // the portal onboarding id (was historically carried by `requestId` before the
  // RequestId/PreVerificationRequestId swap); null for native change-request rows.
  // Used for the onboarding-form navigation.
  preVerificationRequestId: string | null
  requestType: string | null
  status: string | null
  workerFullName: string | null
  requestBy: string | null
  requestDate: string | null
  client: { fboId?: number | null; obCode?: string | null; name?: string | null } | null
  // Nested worker-organization carrier (promoted from the historic flat string per
  // the unified-requests-index migration — mirrors `client`). Selected via the
  // `WorkerOrganization/Name` slash-path; `fboId` is only present once an operator
  // resolves it via the org search.
  workerOrganization: { fboId?: number | null; name?: string | null } | null
  lineOfBusiness: string | null
  workerProfileType: string | null
  startDate: string | null
  endDate: string | null
  billRate: number | null
  billRateCurrencyCode: string | null
  billRateTypeCode: string | null
  payRate: number | null
  payRateCurrencyCode: string | null
  payRateTypeCode: string | null
  version: number | null
  assignedToUserFullName: string | null
  assignedToUserEmail: string | null
  requestSubType: string | null
}

// Column key → Azure Search Index field name (PascalCase).
//
// `clientOrg` maps to `Client/Name` — the structured client carrier on the
// index, slash-path syntax for the sub-field of a complex Edm type. The
// flat `ClientOrganization` column was retired in the gw-sync alignment;
// all filter / facet / sort / select operations against the client name
// now use `Client/Name`. Azure Search supports filter, facet, sort, and
// select on slash-path sub-fields of complex types.
export const COLUMN_API_FIELD_MAP: Record<string, string> = {
  status: 'Status',
  requestType: 'RequestType',
  workerName: 'WorkerFullName',
  requestedByName: 'RequestBy',
  dateRequested: 'RequestDate',
  clientOrg: 'Client/Name',
  // Slash-path sub-field of the complex `WorkerOrganization` type — same pattern as
  // `Client/Name`. Required for select/sort/filter now that WorkerOrganization is a
  // complex Edm type (a bare `WorkerOrganization` would error on sort/filter).
  workerOrg: 'WorkerOrganization/Name',
  lineOfBusiness: 'LineOfBusiness',
  profileType: 'WorkerProfileType',
  startDate: 'StartDate',
  endDate: 'EndDate',
  billRate: 'BillRate',
  assignedToUserFullName: 'AssignedToUserFullName',
}

export const API_SELECT_FIELDS = [
  'id',
  'RequestId',
  'PreVerificationRequestId',
  'RequestType',
  'Version',
  'AssignedToUserEmail',
  'RequestSubType',
  // Companion canonical rate fields — not surfaced in COLUMN_API_FIELD_MAP
  // (no dedicated grid column) but read by the detail panel + modal pre-fill.
  'BillRateCurrencyCode',
  'BillRateTypeCode',
  'PayRate',
  'PayRateCurrencyCode',
  'PayRateTypeCode',
  ...Object.values(COLUMN_API_FIELD_MAP),
].join(',')

export const DEFAULT_FACETS: string[] = [
  'RequestType,count:2000',
  'Status,count:2000',
  'LineOfBusiness,count:2000',
  'WorkerProfileType,count:2000',
  // Slash-path facet on the complex `Client` sub-field. Azure Search returns
  // the facet response keyed by the same literal `Client/Name` string, so
  // the lookup site (`serverFacets[apiField]`) finds it without extra
  // normalization. The Name sub-field is declared facetable in the index
  // schema (gw-client-request-index-config.json) so this resolves.
  'Client/Name,count:2000',
]

export const FACETABLE_FIELDS: string[] = [
  'RequestType',
  'Status',
  'LineOfBusiness',
  'WorkerProfileType',
  'Client/Name',
]

export const DEFAULT_PAGE_SIZE = 25

// Envelope type returned by useClientRequestsApi hook
export interface ClientRequestApiResponse {
  data: ChangeRequestDto[]
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  facets?: Record<string, FacetValue[]>
}

// Normalize an arbitrary API requestType string to the canonical ClientRequestType.
// Handles casing variations ("onboarding" -> "Onboarding") via API_REQUEST_TYPE_MAP, and
// returns undefined for values outside REQUEST_TYPES.
export function resolveClientRequestType(raw: string | null | undefined): ClientRequestType | undefined {
  const trimmed = raw?.trim()
  if (!trimmed) return undefined
  const lower = trimmed.toLowerCase()
  const matchingRequest = REQUEST_TYPES.find(t => t.toLowerCase() === lower)
  return API_REQUEST_TYPE_MAP[lower] ?? matchingRequest
}

// Transform an API search result into the ChangeRequestDto shape used by the grid
export function transformSearchResultToChangeRequest(
  result: ClientRequestSearchResult
): ChangeRequestDto {
  const status = result.status as ClientRequestStatus | null

  return {
    // RequestId is now uniformly the source ClientRequest doc id (the old
    // ClientRequestDocumentId field was removed when the Onboarding overload was
    // resolved). It held the doc id before too, so this is value-preserving.
    changeRequestId: result.requestId ?? undefined,
    requestId: result.requestId ?? undefined,
    // Portal onboarding id for Onboarding rows (the navigation target); null elsewhere.
    preVerificationRequestId: result.preVerificationRequestId ?? undefined,
    status: STATUS_VALUES.includes(status as ClientRequestStatus)
      ? (status as ClientRequestStatus)
      : undefined,
    requestType: resolveClientRequestType(result.requestType),
    // ChangeRequestDto field is named `workerName` for historical reasons
    // (the grid + downstream consumers expect that shape); the underlying
    // canonical source is `WorkerFullName` on the index.
    workerName: result.workerFullName ?? undefined,
    requestedByName: result.requestBy ?? undefined,
    dateRequested: result.requestDate ?? undefined,
    // Read from the structured client carrier — the flat clientOrganization
    // was retired from the index (gw-sync alignment). ChangeRequestDto still
    // uses the legacy `clientOrg` field name internally for back-compat with
    // grid + downstream consumers.
    clientOrg: result.client?.name ?? undefined,
    // Read the name out of the nested worker-organization carrier (mirrors clientOrg).
    workerOrg: result.workerOrganization?.name ?? undefined,
    lineOfBusiness: result.lineOfBusiness ?? undefined,
    profileType: result.workerProfileType ?? undefined,
    startDate: result.startDate ?? undefined,
    endDate: result.endDate ?? undefined,
    billRate: result.billRate ?? undefined,
    version: result.version ?? undefined,
    assignedToUserFullName: result.assignedToUserFullName ?? undefined,
    assignedToUserEmail: result.assignedToUserEmail ?? undefined,
    requestSubType: result.requestSubType ?? undefined,
  }
}

export type ClientRequestColumnType = 'text' | 'date' | 'rate' | 'status'

export interface ClientRequestColumnDef {
  key: keyof ChangeRequestDto
  label: string
  type: ClientRequestColumnType
  filterType?: 'facet' | 'text' | 'date' | 'number'
  codeValueSource?: string
  codeValueLookupType?: 'byCode' | 'byId'
}
